/**
 * **분대 출전 위치**(Todo10, 사용자가 고른 권장안): 우리 출발 지대를 좌·중·우 × 앞·뒤 여섯 칸으로 나누고, 분대마다 한
 * 칸을 고른다. 한 칸에 여럿이어도 된다.
 *
 * 칸으로 고르게 한 까닭: 휴대폰에서 손가락으로 쉽고, 서버가 검증하기 쉽다(칸 번호 0~5뿐). 자유 배치는 바위·경계 검사와
 * 조작 여지가 늘어난다.
 *
 * 고르기 전에는 병사가 분대 구분 없이 한 덩어리에 섞여 서고, 첫 틱부터 대형이 병종을 보고 자리를 갈랐다(`World.placeSlots`의
 * `STATION_*`) — 그래서 첫 몇 초는 우리끼리 엉켜 자리를 찾았다. 배치를 실은 판은 분대가 제 칸에 모여 서고, 대형의 자리도
 * 그 칸(장수 칸에서 본 상대 자리)을 지킨다.
 *
 * 순수 함수만 둔다 — 화면(기본값 보여 주기)과 시나리오(검증·세우기)가 같은 규칙을 쓴다.
 */
import { MAX_SQUADS } from './commands.ts';
import { Branch, TROOP_BY_ID } from './troops.ts';

/** 칸 수. 번호 = 열(0 좌 · 1 중 · 2 우) + 3 × 줄(0 앞 · 1 뒤). */
export const DEPLOY_SLOTS = 6;
export const DeploySlot = {
  LeftFront: 0,
  CenterFront: 1,
  RightFront: 2,
  LeftBack: 3,
  CenterBack: 4,
  RightBack: 5,
} as const;

export const DEPLOY_SLOT_NAMES = ['좌익 앞', '중군 앞', '우익 앞', '좌익 뒤', '중군 뒤', '우익 뒤'] as const;

/**
 * 열 사이(px). 처음에는 200(옛 보병·기병 분대 자리의 가운데쯤)이었는데, 공격 명령 sweep에서 분대가 따로따로 부딪쳐 상점
 * 장비 깊이 15의 장수 생존이 12→5/15로 떨어졌다. 140·줄 사이 80·분대 흩어짐을 넓히자(`squadSpreadPx`) 깊이 15는 돌아왔다.
 * 시드 45개로 다시 잰 값과 사용자 결정은 `docs/design.md` '분대 출전 위치'에 있다(`npm run deploy-probe`).
 */
export const DEPLOY_COL_PX = 140;
/** 앞뒤 줄 사이(px). 옛 궁수 분대 자리(뒤 100)와 같은 결. */
export const DEPLOY_ROW_PX = 80;
/** 같은 칸에 둘째·셋째 분대가 오면 옆으로 비켜 선다(px). 겹쳐 서면 대형 자리가 한 점이 되어 두 분대가 뒤엉켰다. */
export const DEPLOY_SHARE_PX = 70;

/** 장수 분대의 기본 칸. */
export const LEADER_DEFAULT_SLOT = DeploySlot.CenterFront;

/** 주력 가지마다 기본 칸 순서. 옛 자리와 같은 생각 — 보병은 옆(오른쪽 먼저), 궁수는 뒤, 기병은 왼쪽. */
const PREFER: Record<'inf' | 'bow' | 'horse', readonly number[]> = {
  inf: [DeploySlot.RightFront, DeploySlot.LeftFront, DeploySlot.RightBack],
  bow: [DeploySlot.CenterBack, DeploySlot.LeftBack, DeploySlot.RightBack],
  horse: [DeploySlot.LeftFront, DeploySlot.LeftBack, DeploySlot.RightFront],
};

/** 칸 번호가 맞는가(정수 0~5). 리플레이 JSON은 아무 값이나 실어 온다. */
export function isDeploySlot(v: unknown): v is number {
  return typeof v === 'number' && Number.isInteger(v) && v >= 0 && v < DEPLOY_SLOTS;
}

/** 칸의 자리: [옆(오른쪽 +), 앞(+)] px, 중군 앞이 0. */
export function slotOffsetPx(slot: number): [number, number] {
  const col = slot % 3;
  const row = Math.floor(slot / 3);
  // 0 - …: 앞줄이 -0이 되지 않게(시험·저장에서 0과 다르게 보인다).
  return [(col - 1) * DEPLOY_COL_PX, 0 - row * DEPLOY_ROW_PX];
}

/** 분대의 주력 가지(병사 수로). 같으면 보병 — 옆에 서는 것이 가장 덜 위험하다(`World.placeSlots`와 같은 판단). */
export function mainKind(troops: readonly { troop: string; count: number }[]): 'inf' | 'bow' | 'horse' | null {
  let inf = 0;
  let bow = 0;
  let horse = 0;
  for (const t of troops) {
    const b = TROOP_BY_ID.get(t.troop)?.branch;
    if (b === undefined) continue;
    if (b === Branch.Bow) bow += t.count;
    else if (b === Branch.Horse) horse += t.count;
    else inf += t.count;
  }
  if (inf + bow + horse === 0) return null;
  if (horse > inf && horse >= bow) return 'horse';
  if (bow > inf) return 'bow';
  return 'inf';
}

/**
 * 기본 배치. 장수 분대는 중군 앞, 나머지는 주력 가지의 선호 칸 가운데 비어 있는 첫 칸(다 찼으면 첫 칸). 병사가 있는 분대가
 * 먼저 고르고 동료 혼자 선 분대는 뒤에 — `placeSlots`와 같은 까닭(몸이 있는 분대가 먼 자리로 밀리지 않게).
 * `troopsOf(sq)`가 null이면 그 분대는 서지 않는다(-1).
 */
export function autoDeploy(troopsOf: (sq: number) => readonly { troop: string; count: number }[] | null): number[] {
  const out = new Array<number>(MAX_SQUADS).fill(-1);
  out[0] = LEADER_DEFAULT_SLOT;
  const taken = new Set<number>([LEADER_DEFAULT_SLOT]);
  for (let pass = 0; pass < 2; pass++) {
    for (let sq = 1; sq < MAX_SQUADS; sq++) {
      const t = troopsOf(sq);
      if (t === null) continue;
      const kind = mainKind(t);
      if ((kind === null) !== (pass === 1)) continue;
      const prefer = PREFER[kind ?? 'inf'];
      const slot = prefer.find((s) => !taken.has(s)) ?? prefer[0];
      taken.add(slot);
      out[sq] = slot;
    }
  }
  return out;
}

/**
 * 고른 배치를 거른다: 칸 번호가 아니면 기본값으로. 장수 분대(0)는 늘 칸이 있다. 서지 않는 분대는 -1.
 * 길이가 MAX_SQUADS가 아니어도 받는다(모자라면 기본값).
 */
export function sanitizeDeploy(raw: unknown, auto: readonly number[]): number[] {
  const list = Array.isArray(raw) ? raw : [];
  return auto.map((a, sq) => (a < 0 ? -1 : isDeploySlot(list[sq]) ? (list[sq] as number) : a));
}

/**
 * 분대마다 칸 안의 자리(px, [옆, 앞], 중군 앞이 0). 같은 칸의 둘째부터는 옆으로 비켜 선다 — 가운데 쪽 먼저(오른쪽 열은
 * 왼쪽으로, 왼쪽 열은 오른쪽으로, 가운데 열은 번갈아). 장수 분대가 앞선다(색인 순).
 */
export function deployOffsets(slots: readonly number[]): ([number, number] | null)[] {
  const seen = new Array<number>(DEPLOY_SLOTS).fill(0);
  return slots.map((slot) => {
    if (!isDeploySlot(slot)) return null;
    const [x, y] = slotOffsetPx(slot);
    const k = seen[slot]++;
    if (k === 0) return [x, y];
    const col = slot % 3;
    const dir = col === 0 ? 1 : col === 2 ? -1 : k % 2 === 1 ? 1 : -1;
    const step = col === 1 ? Math.ceil(k / 2) : k;
    return [x + dir * step * DEPLOY_SHARE_PX, y];
  });
}
